/**
 * The named Ember humanoids: what `/ember appearance <name>` puts a player into.
 *
 * Each is a record of data -- a look and optional proportions -- and every frame of every animation
 * is generated from it. Adding a character is adding an entry.
 */
import { EmberLook } from "./body"
import { Proportions } from "./rig"

export interface EmberHumanoid {
    name: string
    look: EmberLook
    proportions?: Partial<Proportions>
    /** An EMBER_STYLES name (styles.ts); LPC's by default. */
    style?: string
    /** How it carries itself over every clip: `shamble`, a zombie's (clips.ts shambling). */
    posture?: "shamble"
    /** The smallest frame it is drawn on: a body taller than the 64px frame allows needs more room. */
    frame?: number
}

/** A slim adult man, drawn about 55px tall: `male-slim`, and the skeleton and zombie (his bones, his corpse). */
const SLIM_MALE_PROPORTIONS: Partial<Proportions> = {
    ankleHeight: 2.2,
    shin: 13.75,
    thigh: 13.75,
    hipWidth: 2.5,
    hipDrop: 1.76,
    spine: 7.5,
    neck: 8.14,
    headCenter: 4.9,
    shoulderWidth: 6.2,
    shoulderHeight: 2.7,
    upperArm: 10.56,
    forearm: 8.14,
    footLength: 4.73,
    head: [3.65, 3.6, 3.55],
    craniumLift: 0.45,
    earHeight: -0.7,
    eyeHeight: 0.8,
    eyeSpacing: 1.55,
    profileEyeInset: 0.9,
    torso: [6.2, 6.0, 4.4],
    girdle: { radii: [7.8, 6.2, 3.2], drop: 1.6, cut: 0 },
    deltoidRadius: 2.5,
    pelvis: [4.7, 4.4, 3.6],
    posture: 0.33,
    neckRadius: 2.4,
    upperArmRadius: 2.5,
    forearmRadius: 2.1,
    handRadius: 2.6,
    handInset: 0.0,
    handLength: 1.0,
    thighRadius: 3.0,
    shinRadius: 2.4,
    footRadius: 1.32,
    footWidth: 2.2,
    footProfileWidth: 1.54,
    headRoundness: [2.2, 2.3, 2.1],
    torsoRoundness: [2.2, 2.0, 2.0],
    pelvisRoundness: [2.4, 2.0, 2.0],
    clothThickness: 0.5,
    stance: {
        front: { pitch: 0.05, abduct: 0.22, flex: 0.2, feetOut: 2.6 },
        back: { pitch: 0.05, abduct: 0.22, flex: 0.2, feetOut: 2.6 },
        side: { pitch: 0, abduct: 0.05, flex: 0.15, feetOut: 0.33, feetSplit: 1.1 },
    },
}

/**
 * A real adult woman's build beside the slim man's: about 92% of his height, narrower in the
 * shoulders and ribcage, the pelvis wider than her waist, slimmer arms, neck, hands and feet, and a
 * bust. The same rig and clips: she walks, fights and dresses as he does. Drawn 8% larger
 * (REALIST_FEMALE_PROPORTIONS), so she stands about his height beside the heavier human man.
 */
const WOMAN_PROPORTIONS: Partial<Proportions> = {
    ...SLIM_MALE_PROPORTIONS,
    ankleHeight: 2.0,
    shin: 12.65,
    thigh: 12.65,
    hipWidth: 3.2,
    hipDrop: 1.7,
    spine: 6.9,
    neck: 7.5,
    headCenter: 4.6,
    shoulderWidth: 5.1,
    shoulderHeight: 2.4,
    upperArm: 9.7,
    forearm: 7.5,
    footLength: 4.2,
    head: [3.45, 3.45, 3.4],
    eyeSpacing: 1.45,
    // An hourglass: a slim ribcage; a pelvis wider than it, set low so it is widest at the hip
    // joints and tapers up into the waist; thighs set wide, so the hips flare below the waist.
    torso: [4.7, 5.3, 3.8],
    girdle: { radii: [6.2, 5.2, 2.7], drop: 1.5, cut: 0 },
    deltoidRadius: 1.9,
    // Tall and set low: its top at the waist turns down steeply, so the light doesn't catch a
    // shelf there that reads as a belly.
    pelvis: [5.6, 5.1, 3.8],
    pelvisRoundness: [2.1, 2.0, 2.0],
    pelvisLow: 2.1,
    waist: [3.3, 2.7, 2.9],
    waistHeight: 2.4,
    neckRadius: 1.9,
    upperArmRadius: 1.9,
    forearmRadius: 1.6,
    handRadius: 2.1,
    handLength: 0.9,
    thighRadius: 3.1,
    shinRadius: 2.1,
    footRadius: 1.15,
    footWidth: 1.9,
    footProfileWidth: 1.35,
    bust: 2.55,
    stance: {
        front: { pitch: 0.05, abduct: 0.24, flex: 0.2, feetOut: 2.0 },
        back: { pitch: 0.05, abduct: 0.24, flex: 0.2, feetOut: 2.0 },
        side: { pitch: 0, abduct: 0.05, flex: 0.15, feetOut: 0.3, feetSplit: 1.0 },
    },
}

/**
 * A build scaled whole: every length and girth by `k` -- not its roundness, cloth or stance angles --
 * so the same body comes out larger or smaller.
 */
const scaledBy = (p: Partial<Proportions>, k: number): Partial<Proportions> => {
    const unscaled = new Set(["headRoundness", "torsoRoundness", "pelvisRoundness", "clothThickness"])
    const out: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(p)) {
        if (unscaled.has(key)) out[key] = value
        else if (typeof value === "number") out[key] = value * k
        else if (Array.isArray(value)) out[key] = value.map((v: number) => v * k)
        else if (key === "girdle") {
            const girdle = value as Proportions["girdle"]
            out[key] = { ...girdle, radii: girdle.radii.map(v => v * k), drop: girdle.drop * k, cut: girdle.cut * k }
        } else if (key === "stance") {
            const scaledPose = (pose: Record<string, number>) => ({
                ...pose,
                ...(pose.feetOut !== undefined ? { feetOut: pose.feetOut * k } : {}),
                ...(pose.feetSplit !== undefined ? { feetSplit: pose.feetSplit * k } : {}),
            })
            out[key] = Object.fromEntries(Object.entries(value as object).map(([facing, pose]) => [facing, scaledPose(pose)]))
        } else out[key] = value
    }
    return out as Partial<Proportions>
}

/** The woman as drawn: 8% larger than her build above (about 55px), the slim man's height. */
const REALIST_FEMALE_PROPORTIONS = scaledBy(WOMAN_PROPORTIONS, 1.08)

/**
 * The human man every human man is drawn as (`realist`): a barbarian's build, some 8% taller than
 * the slim man (about 59px) and far heavier -- broad shoulders under big deltoids and a heavy
 * trapezius, a deep wide chest with slab pectorals tapering to a narrower waist, thick neck, arms,
 * thighs and calves -- the head a touch smaller for the size.
 */
const HUMAN_MALE_PROPORTIONS: Partial<Proportions> = {
    ...SLIM_MALE_PROPORTIONS,
    ankleHeight: 2.35,
    shin: 14.85,
    thigh: 14.85,
    hipWidth: 3.1,
    hipDrop: 1.9,
    spine: 8.1,
    neck: 8.7,
    headCenter: 5.1,
    shoulderWidth: 7.8,
    shoulderHeight: 2.6,
    upperArm: 11.4,
    forearm: 8.8,
    footLength: 5.1,
    head: [3.75, 3.7, 3.65],
    eyeSpacing: 1.6,
    torso: [7.6, 6.9, 5.2],
    girdle: { radii: [9.8, 7.2, 3.9], drop: 1.4, cut: 0 },
    deltoidRadius: 3.4,
    pelvis: [5.2, 4.7, 4.0],
    neckRadius: 3.1,
    upperArmRadius: 3.4,
    forearmRadius: 2.85,
    handRadius: 3.0,
    handLength: 1.1,
    thighRadius: 3.9,
    shinRadius: 3.0,
    footRadius: 1.5,
    footWidth: 2.6,
    footProfileWidth: 1.75,
    pecs: 2.6,
    stance: {
        // Arms held out from the bulk of the lats and chest; feet set wide under the weight.
        front: { pitch: 0.05, abduct: 0.34, flex: 0.22, feetOut: 3.0 },
        back: { pitch: 0.05, abduct: 0.34, flex: 0.22, feetOut: 3.0 },
        side: { pitch: 0, abduct: 0.07, flex: 0.18, feetOut: 0.4, feetSplit: 1.2 },
    },
}

export const EMBER_HUMANOIDS: Record<string, EmberHumanoid> = {
    // Ember's own man: a real adult's proportions -- about seven heads tall, elbows at the waist,
    // wrists at the crotch, fingertips at mid-thigh -- with a barbarian's build, in Ember's own style,
    // at LPC's camera. Bare, for judging the body; `realist-dressed` is him in clothes.
    realist: {
        name: "realist",
        style: "realist",
        look: { skin: "light" },
        proportions: HUMAN_MALE_PROPORTIONS,
        frame: 80,
    },
    // The slim man, bare and dressed: the realist man as first drawn. The Ember Lord is one.
    "male-slim": {
        name: "male-slim",
        style: "realist",
        look: { skin: "light" },
        proportions: SLIM_MALE_PROPORTIONS,
    },
    "male-slim-dressed": {
        name: "male-slim-dressed",
        style: "realist",
        look: {
            skin: "light",
            hair: { style: "short", color: "brown" },
            shirt: { dye: "teal", sleeves: "long" },
            pants: { dye: "brown" },
            shoes: { dye: "leather", cut: "boots" },
            belt: { dye: "black" },
        },
        proportions: SLIM_MALE_PROPORTIONS,
    },
    // The realist woman, bare (clothes, hair and skin come from the entity), and dressed.
    "realist-female": {
        name: "realist-female",
        style: "realist",
        look: { skin: "light", eyes: "lash" },
        proportions: REALIST_FEMALE_PROPORTIONS,
    },
    "realist-female-dressed": {
        name: "realist-female-dressed",
        style: "realist",
        look: {
            skin: "light",
            eyes: "lash",
            hair: { style: "long", color: "chestnut" },
            shirt: { dye: "maroon", sleeves: "short", neck: "scoop", puffed: true },
            pants: { dye: "brown", cut: "skirt" },
            shoes: { dye: "leather" },
            belt: { dye: "black" },
        },
        proportions: REALIST_FEMALE_PROPORTIONS,
    },
    // The slim man's skeleton: the same rig and proportions, bone where the flesh was, so it walks,
    // fights and wears equipment as he does.
    skeleton: {
        name: "skeleton",
        style: "realist",
        look: { skin: "light", bones: true },
        proportions: SLIM_MALE_PROPORTIONS,
    },
    // The slim man dead and risen: grey-green flesh torn open, sunken eyes, ragged shorts, the
    // shamble -- hunched, arms reaching -- over his own walk, run and strike.
    zombie: {
        name: "zombie",
        style: "realist",
        posture: "shamble",
        look: { skin: "zombie", eyes: "zombie", wounds: true, pants: { dye: "brown", cut: "shorts" } },
        proportions: SLIM_MALE_PROPORTIONS,
    },
    "realist-dressed": {
        name: "realist-dressed",
        style: "realist",
        look: {
            skin: "light",
            hair: { style: "short", color: "brown" },
            shirt: { dye: "teal", sleeves: "long" },
            pants: { dye: "brown" },
            shoes: { dye: "leather", cut: "boots" },
            belt: { dye: "black" },
        },
        proportions: HUMAN_MALE_PROPORTIONS,
        frame: 80,
    },
    wanderer: {
        name: "wanderer",
        look: {
            skin: "light",
            hair: { style: "short", color: "brown" },
            shirt: { dye: "green", sleeves: "short" },
            pants: { dye: "brown" },
            shoes: { dye: "leather", cut: "boots" },
            belt: { dye: "leather" },
        },
    },
    warden: {
        name: "warden",
        look: {
            skin: "brown",
            hair: { style: "crop", color: "black" },
            shirt: { dye: "maroon", sleeves: "long", tunic: true },
            pants: { dye: "charcoal" },
            shoes: { dye: "black", cut: "boots" },
            belt: { dye: "ochre" },
        },
        proportions: { shoulderWidth: 10.2, torso: [8.6, 6.8, 8.6] },
    },
    forager: {
        name: "forager",
        look: {
            skin: "olive",
            hair: { style: "long", color: "blonde" },
            shirt: { dye: "blue", sleeves: "long" },
            pants: { dye: "tan" },
            shoes: { dye: "brown" },
        },
        proportions: { shoulderWidth: 9.0, torso: [7.6, 6.4, 7.7], hipWidth: 4.4 },
    },
    runner: {
        name: "runner",
        look: {
            skin: "black",
            hair: { style: "ponytail", color: "red" },
            shirt: { dye: "white", sleeves: "none" },
            pants: { dye: "navy", cut: "shorts" },
            shoes: { dye: "gray" },
        },
    },
}

export const EMBER_HUMANOID_NAMES = Object.keys(EMBER_HUMANOIDS)

export const isEmberHumanoid = (name: string | undefined): boolean => !!name && name in EMBER_HUMANOIDS

/** An appearance naming this is drawn with its LPC body even where an Ember one exists. */
export const LPC_BODY = "lpc"

/**
 * The Ember humanoid an entity is drawn as: the one its appearance names, else the one for its
 * race -- the realist builds for a human man or woman (clothes, hair and skin come from the entity),
 * the skeleton, the zombie -- or none, its LPC body, where Ember has no body for its race yet or
 * where it asked for LPC.
 */
export const emberSpriteFor = (appearance?: { emberSprite?: string; race?: string; sex?: string } | null): string | undefined => {
    if (!appearance || appearance.emberSprite === LPC_BODY) return undefined
    if (isEmberHumanoid(appearance.emberSprite)) return appearance.emberSprite
    if (appearance.race === "skeleton") return "skeleton"
    if (appearance.race === "zombie") return "zombie"
    if (appearance.race === "human") return appearance.sex === "female" ? "realist-female" : "realist"
    return undefined
}
